import { type RefObject, useEffect, useRef } from "react";

/** A press this far from the pane's right edge is on its scrollbar, whatever the scrollbar measures. */
const MINIMUM_SCROLLBAR_WIDTH = 16;

/**
 * Whether a pointer event landed on the pane's vertical scrollbar.
 *
 * The scrollbar's own width, measured as the pane's border box less its content box, because the panes
 * that use this style theirs wider than a fixed 16px: a press on the left of a 20px bar did nothing.
 */
export function isPointerOnScrollbar(pane: HTMLElement, event: MouseEvent): boolean {
  // A press on a scrollbar lands on the element that scrolls, never on its content. Without this, the
  // 16px minimum below reached over whatever sat at the content's right edge wherever the scrollbar is
  // an overlay taking no width: a comment row's Mark Unread and Delete controls took the press for a
  // scrollbar's, and their click was stopped before it got to them.
  if (event.target !== pane) return false;
  const { left, right } = pane.getBoundingClientRect();
  // A pane with no width has no scrollbar; without this every press on an unlaid-out pane, at x = 0,
  // fell inside the 16px minimum measured back from its right edge, also at 0.
  if (right - left <= 0) return false;
  const scrollbarWidth = Math.max(pane.offsetWidth - pane.clientWidth, MINIMUM_SCROLLBAR_WIDTH);
  return event.clientX >= right - scrollbarWidth && event.clientX <= right;
}

/**
 * Calls `onPress` when the user grabs the pane's scrollbar, which is how a timeline scrubber comes up
 * without being asked for.
 *
 * The click that ends the same press is stopped at the pane: it would otherwise reach the timeline's
 * click-away listener on the document and close the timeline the press had just opened. Both
 * listeners capture, so a child that stops propagation cannot hide the press.
 *
 * The pane must be mounted when the component calling this mounts.
 */
export default function useScrollbarPress(paneRef: RefObject<HTMLElement | null>, onPress: () => void): void {
  const onPressRef = useRef(onPress);

  useEffect(() => {
    onPressRef.current = onPress;
  }, [onPress]);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (isPointerOnScrollbar(pane, event)) onPressRef.current();
    };
    const handleClick = (event: MouseEvent) => {
      if (isPointerOnScrollbar(pane, event)) event.stopPropagation();
    };

    pane.addEventListener("pointerdown", handlePointerDown, true);
    pane.addEventListener("click", handleClick, true);
    return () => {
      pane.removeEventListener("pointerdown", handlePointerDown, true);
      pane.removeEventListener("click", handleClick, true);
    };
  }, [paneRef]);
}
